/**
 * Small building blocks shared by the Equipment views (Equipment.tsx,
 * EquipmentTransfer.tsx, EquipmentReturn.tsx).
 */
import type { JSX } from "preact";
import { useMemo } from "preact/hooks";
import { state } from "../state/store";
import { displayName, bySurname, activePeople } from "../utils/names";
import type { BookingStatus } from "../utils/equipment";

export const STATUS_CLS: Record<BookingStatus, string> = {
  PENDING: "eqs-pending",
  APPROVED: "eqs-approved",
  COLLECTED: "eqs-collected",
  RETURN_PENDING: "eqs-return-pending",
  RETURNED: "eqs-returned",
  REJECTED: "eqs-rejected",
  CANCELLED: "eqs-cancelled",
};

export function nowIso(): string {
  return new Date().toISOString();
}

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Wed 17 Sep 2026" from an ISO date or timestamp. */
export function fmtDate(iso: string): string {
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${WD[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}`;
}

/** "17 Sep 2026, 15:04" from a timestamp. */
export function fmtStamp(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Backdrop close that only fires when the press STARTED on the backdrop
 *  too. Selecting text in a field and releasing the mouse outside the
 *  dialog produces a click on the backdrop — that must not close it. */
export function backdropHandlers(onClose: () => void): {
  onMouseDown: (e: MouseEvent) => void;
  onClick: (e: MouseEvent) => void;
} {
  let armed = false;
  return {
    onMouseDown: (e) => { armed = e.target === e.currentTarget; },
    onClick: (e) => {
      if (armed && e.target === e.currentTarget) onClose();
      armed = false;
    },
  };
}

/** Roster person picker used wherever a name is recorded (collected /
 *  returned / General Affairs supervisor / new holder). Values are
 *  roster names. `exclude` hides people who can't be chosen. */
export function PersonSelect({
  label, value, onChange, wide, exclude, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  wide?: boolean;
  exclude?: string[];
  placeholder?: string;
}): JSX.Element {
  const s = state.value;
  const people = useMemo(() => {
    const list = activePeople(s.roster).sort(bySurname).filter((p) => !exclude?.includes(p));
    if (value && !list.includes(value)) list.unshift(value);
    return list;
  }, [s.roster, value, exclude]);
  return (
    <label class={`eq-field${wide ? " eq-field-wide" : ""}`}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange((e.target as HTMLSelectElement).value)}>
        {!value && <option value="">{placeholder ?? "— choose a person —"}</option>}
        {people.map((p) => <option key={p} value={p}>{displayName(p)}</option>)}
      </select>
    </label>
  );
}

/** Trigger a browser download of in-memory bytes. */
export function saveBytes(bytes: Uint8Array, name: string, type: string): void {
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Open in-memory bytes in a new tab (PDF preview). False if blocked. */
export function openBytes(bytes: Uint8Array, type: string): boolean {
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], { type });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return !!w;
}
